import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  EventType,
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration,
  type EventMessage,
} from "@azure/msal-browser";
import type { AuthConfig } from "@/lib/env";

let instance: PublicClientApplication | null = null;
let apiScope: string | null = null;

export function buildMsalConfig(config: AuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      // Single tenant: staff and B2B guests both sign in against the Datanomy tenant.
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
  };
}

/** Create, initialise and return the MSAL instance (idempotent). */
export async function initMsal(config: AuthConfig): Promise<PublicClientApplication> {
  if (instance) return instance;
  const pca = new PublicClientApplication(buildMsalConfig(config));
  await pca.initialize();

  pca.addEventCallback((event: EventMessage) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload
    ) {
      const result = event.payload as AuthenticationResult;
      if (result.account) pca.setActiveAccount(result.account);
    }
  });

  // Completes a pending redirect (login or token) and picks the active account.
  const redirectResult = await pca.handleRedirectPromise();
  // Any pending redirect is now handled. A leftover "interaction in progress" flag (from a sign-in that was
  // interrupted, e.g. the tab was closed on the Microsoft page) would otherwise block every later sign-in with
  // `interaction_in_progress` and leave the app on a blank splash.
  clearStaleInteraction();
  if (redirectResult?.account) {
    pca.setActiveAccount(redirectResult.account);
  } else if (!pca.getActiveAccount()) {
    const [first] = pca.getAllAccounts();
    if (first) pca.setActiveAccount(first);
  }

  instance = pca;
  apiScope = config.apiScope;
  return pca;
}

function clearStaleInteraction(): void {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith("msal.") && key.endsWith("interaction.status")) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: nothing to clear.
  }
}

let loginStarted = false;

/**
 * Start the sign-in redirect once per page load. AuthGate (whose effect React may run twice in development)
 * and the API client both call this, so only the first call navigates away.
 */
export function startLogin(prompt?: "select_account"): void {
  if (!instance || loginStarted) return;
  loginStarted = true;
  const pca = instance;
  const request = { scopes: getApiScopes(), prompt };
  pca.loginRedirect(request).catch((error: unknown) => {
    if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.interactionInProgress) {
      // Stale flag set after start-up (another tab, a cancelled redirect): clear it and try once more.
      clearStaleInteraction();
      void pca.loginRedirect(request).catch(() => {
        loginStarted = false;
      });
      return;
    }
    loginStarted = false;
    console.error("Sign-in could not start", error);
  });
}

export function getMsalInstance(): PublicClientApplication | null {
  return instance;
}

export function getApiScopes(): string[] {
  return apiScope ? [apiScope] : [];
}
